package dev.lingvo.site.pages.app.settings.webhooks

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.navigation.Link
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.loadReloadable
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LAsyncContent
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LConfirmDialog
import dev.lingvo.site.ds.LEmptyState
import dev.lingvo.site.ds.LEmptyStateIcon
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LOneTimeSecret
import dev.lingvo.site.ds.LSectionTitle
import dev.lingvo.site.ds.LTable
import dev.lingvo.site.ds.LTableBody
import dev.lingvo.site.ds.LTableCell
import dev.lingvo.site.ds.LTableHead
import dev.lingvo.site.ds.LTableHeaderCell
import dev.lingvo.site.ds.LTableHeaderScope
import dev.lingvo.site.ds.LTableRow
import dev.lingvo.site.ds.LTimeStyle
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.formatUtc
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.pages.app.projects.slug.settings.settingsOperationResult
import dev.lingvo.site.state.WorkspaceSelection
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * One webhook: what it subscribes to, and what has actually been sent to it.
 *
 * A webhook is a thing somebody sets up once and stops thinking about, so the whole value of this
 * page is in the failures. **"Finished" and "arrived" are different facts** and the table says which
 * happened: a delivery that burns through its six attempts also stops being pending, and a page that
 * conflated the two would report a dead endpoint as a working one.
 *
 * The payload is not shown. It is customer content, and the questions here are "did it arrive",
 * "what came back" and "when" — none of which need the body.
 */
@Page("/app/settings/webhooks/{webhookId}")
@Layout(".components.layouts.AppLayout")
@Composable
fun WebhookDetailPage() {
    val ctx = rememberPageContext()
    val webhookId = ctx.route.params["webhookId"].orEmpty()
    val scope = rememberCoroutineScope()
    val workspace = WorkspaceSelection.current()
    val slug = workspace?.slug

    var refreshKey by remember { mutableStateOf(0) }
    var rotated by remember { mutableStateOf<String?>(null) }
    var busy by remember { mutableStateOf(false) }
    // Which delivery is being replayed, rather than a bare "something is working". One flag for the
    // whole page meant sending one row again greyed out the rotate button and every other row's
    // button, so the page looked as though replaying a delivery had locked the webhook.
    var resending by remember { mutableStateOf<String?>(null) }
    var confirmingRotate by remember { mutableStateOf(false) }
    var message by remember { mutableStateOf<String?>(null) }
    var note by remember { mutableStateOf<String?>(null) }

    // `loadReloadable`, not `loadAsync(…, refreshKey)`. The counter as an identity key threw both
    // reads away before the re-read was sent, so pressing Redeliver on a failed row replaced the
    // delivery table — the only place the outcome can be read — with skeleton rows, taking the
    // pressed row and the reader's focus with it.
    val hookReload = loadReloadable(slug, reloadToken = refreshKey) {
        slug?.let { ApiClient.webhooks(it) }
    }
    val deliveryReload = loadReloadable(slug, webhookId, reloadToken = refreshKey) {
        slug?.let { ApiClient.webhookDeliveries(it, webhookId) }
    }
    val hooks = hookReload.state
    val deliveries = deliveryReload.state

    Column(Modifier.fillMaxWidth().gap(Space.XL)) {
        LAsyncContent(
            hooks,
            rows = 3,
            loadingLabel = t("app.settings.webhooks.webhook_id.loading_the_webhook", "Loading the webhook"),
            onRetry = { refreshKey++ },
            gap = Space.XL,
        ) { allHooks ->
            val hook = allHooks.orEmpty().firstOrNull { it.id == webhookId }
            if (hook == null) {
                PageHeader(t("app.settings.webhooks.webhook_id.webhook", "Webhook"), "")
                LAlert(t("app.settings.webhooks.webhook_id.this_webhook_is_gone", "This webhook is gone"), t("app.settings.webhooks.webhook_id.it_was_deleted", "It was deleted."), variant = LAlertVariant.INFO)
                Link(path = "/app/settings/webhooks") { Text(t("app.settings.webhooks.webhook_id.back_to_webhooks", "Back to webhooks")) }
                return@LAsyncContent
            }

            PageHeader(t("app.settings.webhooks.webhook_id.webhook", "Webhook"), t("app.settings.webhooks.webhook_id.what_it_subscribes_to_and_everything", "What it subscribes to, and everything sent to it."))
            // Beside what is still on screen, never in place of it: a re-read that fails after a
            // save or a redelivery leaves the last good answer up with a notice on it.
            hookReload.refreshError?.let { failure ->
                LAlert(
                    t("app.settings.webhooks.webhook_id.showing_the_last_answer_that_loaded", "Showing the last answer that loaded"),
                    failure.readable(),
                    variant = LAlertVariant.WARNING,
                    dense = true,
                    live = true,
                )
            }
            // Both only ever appear in answer to a click — a failed toggle, a queued replay —
            // so they are announced. A reader who just pressed "Send again" otherwise gets no
            // acknowledgement at all that anything happened.
            message?.let {
                LAlert(
                    t("app.settings.webhooks.webhook_id.that_didnt_work", "That didn't work"),
                    it,
                    variant = LAlertVariant.WARNING,
                    live = true,
                )
            }
            note?.let {
                LAlert(
                    t("app.settings.webhooks.webhook_id.done", "Done"),
                    it,
                    variant = LAlertVariant.SUCCESS,
                    live = true,
                )
            }

            LCard(Modifier.fillMaxWidth()) {
                Column(Modifier.fillMaxWidth().gap(Space.SM)) {
                    Row(Modifier.fillMaxWidth().gap(Space.SM), verticalAlignment = Alignment.CenterVertically) {
                        // **Plain text, and it must stay plain text.** This string is typed in
                        // by a customer and rendered to whoever is looking at their workspace —
                        // usually support, in a tab that is already authenticated. An anchor
                        // here is a one-click request to an address of their choosing carrying
                        // whatever an authenticated browser carries. It reads like an oversight
                        // that it is not a link. It is not one.
                        Span(Modifier.flexGrow(1).monoFont().toAttrs()) { Text(hook.url) }
                        if (!hook.enabled) LBadge(t("app.settings.webhooks.webhook_id.off", "off"), variant = LBadgeVariant.WARNING)
                    }
                    Row(Modifier.fillMaxWidth().gap(Space.XS)) {
                        hook.events.forEach { LBadge(it.id) }
                    }
                    hook.events.forEach { LMuted(tf("app.settings.webhooks.webhook_id.event_and_description", "{event} — {description}", "event" to it.id, "description" to it.description)) }
                    LMuted(webhookLastResponseLine(hook.lastStatus))
                    if (!hook.enabled) {
                        LAlert(
                            t("app.settings.webhooks.webhook_id.we_switched_this_off", "We switched this off"),
                            // A count in a sentence is a plural, even when the number that
                            // reaches it in practice is always above one. "It failed 1 times" is
                            // the sentence this renders the first time somebody lowers the
                            // threshold, and no test would have caught it.
                            tPlural(
                                "app.settings.webhooks.webhook_id.it_failed_count_times_in_a_row",
                                hook.consecutiveFailures,
                                "It failed {count} time in a row, so we stopped retrying rather " +
                                    "than hammering an endpoint that may be gone. Nothing was lost " +
                                    "— turn it back on once the receiver is fixed.",
                                "It failed {count} times in a row, so we stopped retrying rather " +
                                    "than hammering an endpoint that may be gone. Nothing was lost " +
                                    "— turn it back on once the receiver is fixed.",
                            ),
                            variant = LAlertVariant.WARNING,
                        )
                    }
                    LButton(
                        if (hook.enabled) {
                            t("app.settings.webhooks.webhook_id.turn_it_off", "Turn it off")
                        } else {
                            t("app.settings.webhooks.webhook_id.turn_it_back_on", "Turn it back on")
                        },
                        {
                            scope.launch {
                                busy = true
                                message = settingsOperationResult {
                                    ApiClient.setWebhookEnabled(slug!!, hook.id, !hook.enabled)
                                }.fold({ refreshKey++; null }, { it.message })
                                busy = false
                            }
                        },
                        variant = LButtonVariant.SECONDARY,
                        enabled = !busy,
                    )

                    // **The only repair for a signature a receiver cannot verify.** Webhooks
                    // created before the secret was stored in a signable form are signed with a
                    // value nobody can reproduce; their plaintext is gone, so there is nothing
                    // to migrate — only to reissue. It is also the ordinary move when a secret
                    // leaks, which previously meant deleting the webhook and losing its history.
                    rotated?.let { secret ->
                        LAlert(
                            t("app.settings.webhooks.webhook_id.your_new_signing_secret", "Your new signing secret"),
                            t("app.settings.webhooks.webhook_id.shown_once_put_it_in_the", "Shown once. Put it in the receiver before the next delivery — " +
                                "anything signed with the old one will stop verifying."),
                            variant = LAlertVariant.WARNING,
                            live = true,
                        )
                        // The same component the creation flow uses. A bare `<pre>` left the
                        // one value Lingvo will never show again reachable only by dragging a
                        // selection across a mono string — no copy button, on a secret the
                        // receiver has to hold before the next delivery lands.
                        LOneTimeSecret(
                            value = secret,
                            label = tr("app.settings.webhooks.webhook_id.webhook_signing_secret", "Webhook signing secret"),
                            copyLabel = t("app.settings.webhooks.webhook_id.copy_secret", "Copy secret"),
                            onCopied = { message = null },
                            onCopyFailed = {
                                message = tr(
                                "app.settings.webhooks.webhook_id.couldnt_copy_the_secret_select_it",
                                "Couldn't reach the clipboard. Select the secret above and copy " +
                                    "it manually before leaving.",
                            )
                            },
                        )
                    }
                    LButton(
                        t("app.settings.webhooks.webhook_id.rotate_the_signing_secret", "Rotate the signing secret"),
                        { confirmingRotate = true },
                        variant = LButtonVariant.GHOST,
                        enabled = !busy,
                    )
                    // **The consequence was only ever stated after the fact.** Rotating breaks
                    // every receiver still verifying with the old secret, and the warning saying
                    // so appeared beside the new value — by which time it was already true. One
                    // stray click on a GHOST button was the whole ceremony.
                    LConfirmDialog(
                        open = confirmingRotate,
                        title = t("app.settings.webhooks.webhook_id.rotate_the_signing_secret_2", "Rotate the signing secret?"),
                        confirmLabel = t("app.settings.webhooks.webhook_id.rotate_it", "Rotate it"),
                        onConfirm = {
                            scope.launch {
                                busy = true
                                message = settingsOperationResult {
                                    ApiClient.rotateWebhookSecret(slug!!, hook.id).secret
                                }.fold({ rotated = it; null }, { it.message })
                                busy = false
                                // Closed only once the request has landed, so the confirm
                                // button carries the wait and cannot be pressed twice into two
                                // rotations — the second of which would invalidate a secret the
                                // reader had only just been shown.
                                confirmingRotate = false
                            }
                        },
                        onCancel = { confirmingRotate = false },
                        busy = busy,
                        consequenceTitle = t("app.settings.webhooks.webhook_id.the_old_secret_stops_working_immediately", "The old secret stops working immediately"),
                        consequence = t("app.settings.webhooks.webhook_id.deliveries_after_this_are_signed_with", "Deliveries after this are signed with the new one, so anything still " +
                            "verifying with the old secret starts rejecting them. The new secret is " +
                            "shown once, here, the moment you confirm."),
                    )
                }
            }

            LCard(Modifier.fillMaxWidth()) {
                Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                    LSectionTitle(t("app.settings.webhooks.webhook_id.deliveries", "Deliveries"))
                    deliveryReload.refreshError?.let { failure ->
                        LAlert(
                            t("app.settings.webhooks.webhook_id.showing_the_last_deliveries_that_loaded", "Showing the last deliveries that loaded"),
                            failure.readable(),
                            variant = LAlertVariant.WARNING,
                            dense = true,
                            live = true,
                        )
                    }
                    LAsyncContent(
                        deliveries,
                        rows = 4,
                        loadingLabel = t("app.settings.webhooks.webhook_id.loading_deliveries", "Loading deliveries"),
                        onRetry = { refreshKey++ },
                    ) { deliveryPage ->
                        val rows = deliveryPage?.deliveries.orEmpty()
                        if (rows.isEmpty()) {
                            LEmptyState(
                                t("app.settings.webhooks.webhook_id.nothing_sent_yet", "Nothing sent yet"),
                                t("app.settings.webhooks.webhook_id.events_appear_here_as_they_happen", "Events appear here as they happen. If you expected one, check " +
                                    "that this webhook subscribes to it — a webhook can be set " +
                                    "up correctly and still be listening for the wrong thing."),
                                Modifier.fillMaxWidth(),
                                LEmptyStateIcon.WEBHOOK,
                            ) {}
                        } else {
                            LTable(caption = t("app.settings.webhooks.webhook_id.recent_deliveries", "Recent deliveries")) {
                                LTableHead {
                                    LTableRow {
                                        listOf("When", "Event", "Outcome", "Attempts", "")
                                            .forEach { LTableHeaderCell { Text(it) } }
                                    }
                                }
                                LTableBody {
                                    rows.forEach { delivery ->
                                        LTableRow {
                                            LTableCell {
                                                Text(formatUtc(delivery.createdAt, LTimeStyle.MINUTE))
                                            }
                                            LTableHeaderCell(scope = LTableHeaderScope.ROW) { Text(delivery.event?.id ?: "—") }
                                            LTableCell {
                                                when {
                                                    delivery.succeeded ->
                                                        LBadge(  // i18n-ignore — a protocol name and its numeric status code
                                                            "HTTP ${delivery.status}",
                                                            variant = LBadgeVariant.SUCCESS,
                                                        )
                                                    // Still queued: neither a success nor a
                                                    // failure yet, and saying "failed" here
                                                    // would send somebody debugging a
                                                    // receiver that is about to be called.
                                                    delivery.finishedAt == null ->
                                                        LBadge(t("app.settings.webhooks.webhook_id.waiting", "waiting"))
                                                    else -> LBadge(
                                                        delivery.status?.let { "HTTP $it" }  // i18n-ignore — a protocol name and its numeric status code
                                                            ?: t("app.settings.webhooks.webhook_id.no_response", "no response"),
                                                        variant = LBadgeVariant.DANGER,
                                                    )
                                                }
                                            }
                                            LTableCell { Text(delivery.attempts.toString()) }
                                            LTableCell {
                                                LButton(
                                                    t("app.settings.webhooks.webhook_id.send_again", "Send again"),
                                                    {
                                                        scope.launch {
                                                            resending = delivery.id
                                                            message = settingsOperationResult {
                                                                ApiClient.replayDelivery(
                                                                    slug!!,
                                                                    hook.id,
                                                                    delivery.id,
                                                                )
                                                            }.fold(
                                                                {
                                                                    // One literal, not a `+`
                                                                    // run: the extractor refuses
                                                                    // to key the head of a
                                                                    // concatenation and reports
                                                                    // it as a skipped template,
                                                                    // so this sentence stayed
                                                                    // English in every locale.
                                                                    note = tr(
                                                                        "app.settings.webhooks.webhook_id.queued_it_goes_out_as",
                                                                        "Queued. It goes out as a new delivery, so this row stays as it is.",
                                                                    )
                                                                    refreshKey++
                                                                    null
                                                                },
                                                                { it.message },
                                                            )
                                                            resending = null
                                                        }
                                                    },
                                                    variant = LButtonVariant.GHOST,
                                                    size = LButtonSize.SM,
                                                    // Every row's button reads "Send again";
                                                    // the timestamp and event are what tell
                                                    // one delivery from another, and only
                                                    // the visible table was carrying them.
                                                    accessibleLabel = t("app.settings.webhooks.webhook_id.send_again", "Send again") +
                                                        ": ${delivery.event?.id.orEmpty()} " +
                                                        formatUtc(delivery.createdAt, LTimeStyle.MINUTE),
                                                    enabled = resending == null,
                                                    loading = resending == delivery.id,
                                                )
                                            }
                                        }
                                    }
                                }
                            }
                            rows.firstOrNull { it.error != null }?.error?.let { error ->
                                // The most recent error in full, because a truncated one in a
                                // table cell is exactly the part somebody needs.
                                LMuted(tf(
                                    "app.settings.webhooks.webhook_id.most_recent_error",
                                    "Most recent error: {error}",
                                    "error" to error,
                                ))
                            }
                            LMuted(
                                t("app.settings.webhooks.webhook_id.replaying_sends_the_same_event_again_2", "Replaying sends the same event again as a new delivery. The " +
                                    "original row stays — a log that edits itself when you use " +
                                    "it is not a log."),
                            )
                        }
                    }
                }
            }
            Link(path = "/app/settings/webhooks") { Text(t("app.settings.webhooks.webhook_id.back_to_webhooks", "Back to webhooks")) }
        }
    }
}

/**
 * Whether the endpoint is working, which is the one thing this page exists to answer.
 *
 * Two lines above it the event list already went through `tf`; this one was a raw Kotlin template
 * either way it branched, so "Last response: HTTP 500." and "Nothing has been sent yet." reached
 * every locale in English.
 */
internal fun webhookLastResponseLine(lastStatus: Int?): String = lastStatus
    ?.let {
        trf(
            "app.settings.webhooks.webhook_id.last_response_http_status",
            "Last response: HTTP {status}.",
            "status" to it,
        )
    }
    ?: tr(
        "app.settings.webhooks.webhook_id.nothing_has_been_sent_yet",
        "Nothing has been sent yet.",
    )
